import { FieldModel } from '@cibo/core'
import {
  LandReportMapDataLayerPicker,
  LandReportMapDataLayers,
  useLandReport,
  useLandReportView,
} from '@cibo/landmanager'
import { StaticMapGL } from '@cibo/mapgl'
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan'
import { Button, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material'
import bbox from '@turf/bbox'
import { LAND_MANAGER_ROUTES } from 'navigation/types/routes'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'
import { SectionHeader, SectionHeaderProps } from '../SectionHeader'

interface MapOverviewProps {
  field: FieldModel
  sectionHeaderProps?: Partial<SectionHeaderProps>
}

export const MapOverview = ({ field, sectionHeaderProps }: MapOverviewProps) => {
  const { t } = useTranslation('LandReport/MapOverview')
  const { dataLayer, setDataLayer } = useLandReportView({ resourceId: field.resourceId })

  /**
   * @todo
   * refactor this information into the Field query response
   */
  const f = useLandReport(field.resourceId)

  if (f.isLoading) {
    return null
  }

  if (f.isError) {
    return <Typography>error message</Typography>
  }
  const bounds =
    !!field.geometryDetail?.input?.feature.geometry &&
    bbox(field.geometryDetail.input.feature.geometry)

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <SectionHeader
          {...sectionHeaderProps}
          title={t('acres', { count: field.acres })}
          subtitle={
            !!field.acres ? t('tillableAcres', { count: f.data.tillableAcres / field.acres }) : '--'
          }
        />
        <Stack alignItems="flex-end">
          <Stack direction="row" spacing={0.5}>
            <Typography variant="caption">{t('nccpi')}</Typography>
            <Typography variant="body2">{f.data.nccpi}</Typography>
          </Stack>

          {!!f.data.statePi && (
            <Stack direction="row" spacing={0.5}>
              <Typography variant="caption">
                {t('statePi', { context: f.data.statePiName })}
              </Typography>
              <Typography variant="body2">{f.data.statePi}</Typography>
            </Stack>
          )}
          <Tooltip
            title={
              <Stack spacing={2} divider={<Divider />}>
                <Stack>
                  <Typography>{t('nccpi')}</Typography>
                  <Typography variant="body2">{t('nccpi_definition')}</Typography>
                </Stack>
                {!!f.data.statePiName && (
                  <Stack>
                    <Typography>{t('statePi', { context: f.data.statePiName })}</Typography>
                    <Typography variant="body2">
                      {t('statePi_definition', { context: f.data.statePiName })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            }
          >
            <Typography variant="caption" color={'secondary.main'}>
              {t('about')}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
      <Paper elevation={0} sx={{ overflow: 'hidden' }}>
        <Stack>
          <Stack height={340}>
            {!!bounds && (
              // @ts-ignore BBox3d type confuses this
              <StaticMapGL bounds={bounds} padding={4}>
                <LandReportMapDataLayers
                  dataLayer={dataLayer}
                  field={field}
                  setDataLayer={setDataLayer}
                />
                <Stack position="absolute" top={0} left={0} right={0} bottom={0} padding={2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      component={Link}
                      to={generatePath(LAND_MANAGER_ROUTES.DETAIL_EDIT_BOUNDARY, {
                        resourceId: field.resourceId,
                      })}
                      variant="contained"
                      size="small"
                      color="secondary"
                      disabled={field?.resolveStandingDetail('geometry').immutable}
                    >
                      {t('editBoundaries')}
                    </Button>
                    <Button
                      component={Link}
                      to={generatePath(LAND_MANAGER_ROUTES.DETAIL_MAP, {
                        resourceId: field.resourceId,
                      })}
                      variant="contained"
                      size="small"
                      color="secondary"
                      sx={{ minWidth: 0 }}
                      aria-label={t('expand')}
                    >
                      <SettingsOverscanIcon />
                    </Button>
                  </Stack>
                </Stack>
              </StaticMapGL>
            )}
          </Stack>
        </Stack>
        <LandReportMapDataLayerPicker
          dataLayer={dataLayer}
          setDataLayer={setDataLayer}
          firstButtonProps={{ sx: { borderTopLeftRadius: 0 } }}
          lastButtonProps={{ sx: { borderTopRightRadius: 0 } }}
          size="small"
          fullWidth
        />
      </Paper>
    </Stack>
  )
}
